import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../config/queryClient';

export const useLeadsBoardQuery = (leadStages) => {
  const isEnabled = !!leadStages && Array.isArray(leadStages) && leadStages.length > 0;

  return useQuery({
    queryKey: queryKeys.leads.board(),
    queryFn: async () => {
      try {
        if (leadStages.length === 0) {
          throw new Error('No active lead stages found. Please configure lead stages first.');
        }
        const { listLeadsV2 } = require('services/lead/leadServices');

        const response = await listLeadsV2({
          limit: 15,
          data: { lead_stage_ids: leadStages?.map((stage) => stage.sk) },
          excludeFromLoading: true,
        });

        const initialList = {};
        const pagination = {};

        Object.keys(response?.data).forEach((key) => {
          initialList[key] = response?.data?.[key]?.items || [];
          pagination[key] = {
            page: response?.data?.[key]?.page || 1,
            hasMore: response?.data?.[key]?.has_more || false,
            loading: false,
            total: response?.data?.[key]?.total_count || 0,
          };
        });

        return {
          leads: initialList,
          pagination,
        };
      } catch (error) {
        console.error('Error fetching leads:', error);
        return {
          leads: {},
          pagination: {},
        };
      }
    },
    enabled: isEnabled,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
};

export const useMoveCardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moveData) => {
      const { sourceCardId, sourceColumnId, targetColumnId } = moveData;

      // Skip API call if moving within the same column (just reordering)
      if (sourceColumnId === targetColumnId) {
        console.log('Same column reordering - skipping API call');
        return { success: true, sameColumn: true };
      }

      // Different column - update lead_stage via API
      const { updateLeadDetails } = require('services/lead/leadServices');
      return updateLeadDetails(sourceCardId, { lead_stage: targetColumnId }, true);
    },

    onMutate: async (moveData) => {
      const { sourceCardId, sourceColumnId, targetColumnId, targetCardId, edge, position, filters = {} } = moveData;

      // Cancel queries for affected columns (include filters)
      await queryClient.cancelQueries([...queryKeys.leads.column(sourceColumnId), filters]);
      if (sourceColumnId !== targetColumnId) {
        await queryClient.cancelQueries([...queryKeys.leads.column(targetColumnId), filters]);
      }

      // Snapshot previous values (ONLY INFINITE CACHES NOW!)
      const previousSourceData = queryClient.getQueryData([...queryKeys.leads.column(sourceColumnId), filters]);
      const previousTargetData =
        sourceColumnId === targetColumnId
          ? previousSourceData
          : queryClient.getQueryData([...queryKeys.leads.column(targetColumnId), filters]);

      // Find and extract the moved card
      let movedCard = null;
      let cardWasFound = false;

      // Check if moving within the same column
      const isSameColumn = sourceColumnId === targetColumnId;

      if (isSameColumn) {
        // SAME COLUMN: Reorder without changing totalCount
        queryClient.setQueryData([...queryKeys.leads.column(sourceColumnId), filters], (old) => {
          if (!old?.pages) return old;

          // Flatten all items to find source and target
          let allItems = [];
          old.pages.forEach((page) => {
            allItems = allItems.concat(page.items);
          });

          // Find source and target indices
          const sourceIndex = allItems.findIndex((item) => item.sk === sourceCardId);
          const targetIndex = targetCardId ? allItems.findIndex((item) => item.sk === targetCardId) : -1;

          if (sourceIndex === -1) {
            return old; // Card not found, no changes
          }

          // Remove from source position
          const [removed] = allItems.splice(sourceIndex, 1);

          // Calculate insert position based on edge
          let insertIndex = targetIndex;
          if (targetIndex !== -1) {
            if (edge === 'bottom') {
              insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
            } else {
              // edge === 'top'
              insertIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
            }
          } else if (position === 'end') {
            insertIndex = allItems.length;
          } else {
            insertIndex = 0;
          }

          // Insert at new position
          allItems.splice(insertIndex, 0, removed);

          // Reconstruct pages with reordered items (keep same page structure)
          let itemIndex = 0;
          const updatedPages = old.pages.map((page) => {
            const pageItemCount = page.items.length;
            const pageItems = allItems.slice(itemIndex, itemIndex + pageItemCount);
            itemIndex += pageItemCount;
            return { ...page, items: pageItems };
          });

          return { ...old, pages: updatedPages };
        });
      } else {
        // DIFFERENT COLUMN: Remove from source, add to target

        // UPDATE 1: Remove from source column and update totalCount
        queryClient.setQueryData([...queryKeys.leads.column(sourceColumnId), filters], (old) => {
          if (!old?.pages) return old;

          // First pass: remove card and check if found
          const updatedPages = old.pages.map((page) => {
            const updatedItems = page.items.filter((lead) => {
              if (lead.sk === sourceCardId) {
                movedCard = { ...lead, lead_stage: targetColumnId };
                cardWasFound = true;
                return false; // Remove from source
              }
              return true;
            });

            return { ...page, items: updatedItems };
          });

          // Second pass: update totalCount for all pages if card was found
          const finalPages = updatedPages.map((page) => ({
            ...page,
            totalCount: cardWasFound ? Math.max(0, (page.totalCount || 0) - 1) : page.totalCount,
          }));

          return { ...old, pages: finalPages };
        });

        // UPDATE 2: Add to target column and update totalCount
        if (movedCard) {
          queryClient.setQueryData([...queryKeys.leads.column(targetColumnId), filters], (old) => {
            if (!old?.pages || !old.pages.length) return old;

            const updatedPages = [...old.pages];

            if (position === 'end') {
              // Add to last page
              const lastPageIndex = updatedPages.length - 1;
              updatedPages[lastPageIndex] = {
                ...updatedPages[lastPageIndex],
                items: [...updatedPages[lastPageIndex].items, movedCard],
              };
            } else {
              // Add to first page (most common case)
              updatedPages[0] = {
                ...updatedPages[0],
                items: [movedCard, ...updatedPages[0].items],
              };
            }

            // Update totalCount for all pages (increase by 1)
            const finalPages = updatedPages.map((page) => ({
              ...page,
              totalCount: (page.totalCount || 0) + 1,
            }));

            return { ...old, pages: finalPages };
          });
        }
      }

      return {
        previousSourceData,
        previousTargetData,
        sourceColumnId,
        targetColumnId,
        filters,
      };
    },

    onError: (err, moveData, context) => {
      console.error('Move card failed:', err);

      // Rollback source cache
      if (context?.previousSourceData) {
        queryClient.setQueryData([...queryKeys.leads.column(context.sourceColumnId), context.filters], context.previousSourceData);
      }

      // Rollback target cache (if different)
      if (context?.sourceColumnId !== context?.targetColumnId && context?.previousTargetData) {
        queryClient.setQueryData([...queryKeys.leads.column(context.targetColumnId), context.filters], context.previousTargetData);
      }
    },

    onSettled: (data, error, variables, context) => {
      if (context?.sourceColumnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.leads.column(context.sourceColumnId), context.filters],
        });
      }
      if (context?.targetColumnId && context.targetColumnId !== context.sourceColumnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.leads.column(context.targetColumnId), context.filters],
        });
      }
    },
  });
};

export const useUpdateCardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moveData) => {
      const { cardId, data } = moveData;
      const { updateLeadDetails } = require('services/lead/leadServices');
      return updateLeadDetails(cardId, data, true);
    },

    onMutate: async (moveData) => {
      const { cardId, columnId, data, filters = {} } = moveData;
      console.log(cardId, columnId, data, 'cardId, columnId, data');

      // Cancel outgoing queries to prevent race conditions (include filters)
      await queryClient.cancelQueries([...queryKeys.leads.column(columnId), filters]);

      // Store previous data for rollback (ONLY ONE CACHE NOW!)
      const previousData = queryClient.getQueryData([...queryKeys.leads.column(columnId), filters]);

      // UPDATE: Infinite query cache (ALL pages)
      queryClient.setQueryData([...queryKeys.leads.column(columnId), filters], (old) => {
        console.log(old, 'old');
        if (!old?.pages) return old;

        const r = {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((lead) => (lead.sk === cardId ? { ...lead, ...data } : lead)),
          })),
        };
        console.log(r, 'r');
        return r;
      });

      return { previousData, columnId, filters };
    },

    onError: (err, variables, context) => {
      console.error('Update card failed:', err);

      // Rollback single cache on error
      if (context?.previousData) {
        queryClient.setQueryData([...queryKeys.leads.column(context.columnId), context.filters], context.previousData);
      }
    },

    onSettled: (data, error, variables, context) => {
      // Invalidate the column to refetch fresh data from server
      // IMPORTANT: Include filters in invalidation to target correct cache
      if (context?.columnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.leads.column(context.columnId), context.filters],
        });
      }
    },
  });
};

export const useReorderColumnsMutation = (practice_area, show_unassigned_pa_column) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reorderData, leadStages }) => {
      const { updateCaseStagePosition } = require('services/Settings');

      const { sourceIndex, targetIndex, edge } = reorderData;
      const updatedColumns = [...leadStages];
      const [movedColumn] = updatedColumns.splice(sourceIndex, 1);

      let insertIndex = targetIndex;
      if (edge === 'right') {
        insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
      } else if (edge === 'left') {
        insertIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      }

      updatedColumns.splice(insertIndex, 0, movedColumn);

      updateCaseStagePosition({ updated_positions: updatedColumns?.map((v, i) => ({ ...v, position: i })) }, true);
    },
    onMutate: async ({ reorderData }) => {
      const { sourceIndex, targetIndex, edge } = reorderData;

      await queryClient.cancelQueries(queryKeys.leadStages.list(practice_area, show_unassigned_pa_column));

      const previousData = queryClient.getQueryData(queryKeys.leadStages.list(practice_area, show_unassigned_pa_column));

      queryClient.setQueryData(queryKeys.leadStages.list(practice_area, show_unassigned_pa_column), (old) => {
        if (!old) return old;

        const updatedColumns = [...old];
        const [movedColumn] = updatedColumns.splice(sourceIndex, 1);

        let insertIndex = targetIndex;
        if (edge === 'right') {
          insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
        } else if (edge === 'left') {
          insertIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
        }

        updatedColumns.splice(insertIndex, 0, movedColumn);

        return updatedColumns?.map((v, i) => ({ ...v, position: i }));
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      console.error('Reorder columns failed:', err);
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.leadStages.list(practice_area, show_unassigned_pa_column), context.previousData);
      }
    },
  });
};

export const useDeleteCardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ cardId }) => {
      const { deleteLead } = require('services/lead/leadServices');
      return deleteLead(cardId);
    },

    onMutate: async ({ cardId, columnId, filters = {} }) => {
      // Cancel outgoing queries to prevent race conditions (include filters)
      await queryClient.cancelQueries([...queryKeys.leads.column(columnId), filters]);

      // Snapshot previous data (ONLY ONE CACHE NOW!)
      const previousData = queryClient.getQueryData([...queryKeys.leads.column(columnId), filters]);

      // UPDATE: Infinite cache (ALL pages) and update totalCount
      queryClient.setQueryData([...queryKeys.leads.column(columnId), filters], (old) => {
        if (!old?.pages) return old;

        // Track if card was found and deleted
        let cardWasDeleted = false;

        const updatedPages = old.pages.map((page) => {
          const filteredItems = page.items.filter((lead) => {
            if (lead.sk === cardId) {
              cardWasDeleted = true;
              return false;
            }
            return true;
          });

          return {
            ...page,
            items: filteredItems,
          };
        });

        // Update totalCount for all pages if card was deleted
        const finalPages = updatedPages.map((page) => ({
          ...page,
          totalCount: cardWasDeleted ? Math.max(0, (page.totalCount || 0) - 1) : page.totalCount,
        }));

        return {
          ...old,
          pages: finalPages,
        };
      });

      return { previousData, columnId, filters };
    },

    onError: (err, variables, context) => {
      console.error('Delete card failed:', err);

      // Rollback single cache on error
      if (context?.previousData) {
        queryClient.setQueryData([...queryKeys.leads.column(context.columnId), context.filters], context.previousData);
      }
    },

    onSettled: (data, error, variables, context) => {
      // Invalidate the column to refetch fresh data from server
      // IMPORTANT: Include filters in invalidation to target correct cache
      if (context?.columnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.leads.column(context.columnId), context.filters],
        });
      }
    },
  });
};

export const useRenameColumnMutation = (practice_area, show_unassigned_pa_column) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sk, pk, newName }) => {
      const { updateLeadStage } = require('services/Settings');
      return updateLeadStage({
        pk: pk,
        sk: sk,
        lead_stage_name: newName,
        is_lead: true,
      });
    },
    onMutate: async ({ sk, newName }) => {
      await queryClient.cancelQueries(queryKeys.leadStages.list);

      const previousData = queryClient.getQueryData(queryKeys.leadStages.list(practice_area, show_unassigned_pa_column));

      queryClient.setQueryData(queryKeys.leadStages.list(practice_area, show_unassigned_pa_column), (old) => {
        if (!old) return old;
        return old?.map((stage) => (stage.sk === sk ? { ...stage, lead_stage_name: newName } : stage)) || [];
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      console.error('Rename column failed:', err);
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.leadStages.list(), context.previousData);
      }
    },
  });
};

export const useArchiveColumnMutation = (practice_area, show_unassigned_pa_column) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ columnId, pk }) => {
      const { deleteLeadStage } = require('services/Settings');
      return deleteLeadStage(pk, columnId, 'ARCHIVED', true);
    },
    onMutate: async ({ columnId }) => {
      await queryClient.cancelQueries(queryKeys.leadStages.list);

      const previousData = queryClient.getQueryData(queryKeys.leadStages.list(practice_area, show_unassigned_pa_column));

      queryClient.setQueryData(queryKeys.leadStages.list(practice_area, show_unassigned_pa_column), (old) => {
        if (!old) return old;
        return old?.filter((stage) => stage.sk !== columnId) || [];
      });

      return { previousData };
    },
    onSuccess: () => {
      // Don't invalidate - optimistic updates are enough
      // Both board and leadStages caches are already updated
    },
    onError: (err, variables, context) => {
      console.error('Archive column failed:', err);
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.leadStages.list(), context.previousData);
      }
    },
  });
};
